import { Badge, Colors, Text, TextField, View } from 'react-native-ui-lib';
import { FirestoreConstants, navigationProps, ElementPath, PathConstants, ContainerType } from '../typings/types';
import React, { useEffect } from 'react';
import { useIsMobile } from '../MediaQueries';
import { strings } from '../localization/localization.web';
import { DocumentData, arrayRemove, collection, deleteDoc, doc, getDoc, getDocs, getFirestore, limit, query, setDoc, where } from 'firebase/firestore';
import { IconButton } from './Buttons';
import { getGroupStatementsQuery, getStatementsQuery, getTopicElementCounts, getTopicReference, useUserState } from '../firebaseWrapper/firebaseWrapper';
import { MaterialIcons } from '@expo/vector-icons';
import { FloatingActionButton } from './FloatingActionButton';
import { LazyList } from './FeedList';
import { alert } from './Alert';
import { StatementViewList } from './StatementView';
import { getLocation } from '../helperFunctions';
import { QuestionViewList } from './QuestionView';

export function TopicScreen({
  navigation,
  route,
}: navigationProps): JSX.Element {
  const location = getLocation(navigation);
  const parentInfo: ElementPath = {
    loc: location === PathConstants.GROUP ? PathConstants.GROUP : PathConstants.PUBLIC,
    id: route.params.group,
    type: location === PathConstants.GROUP ? ContainerType.GROUP : undefined,
    groupId: route.params.group
  }
  const groupId = route.params.group;
  const topicId = route.params.id;
  const memberId = route.params.member;
  const isMobile = useIsMobile();
  const [group, setGroup] = React.useState<DocumentData>();
  const [topic, setTopic] = React.useState<DocumentData>();
  const [topicElementCounts, setTopicElementCounts] = React.useState<{ statementCount: number, argumentCount: number }>();
  const [topicNameEditable, setTopicNameEditable] = React.useState(false);
  const [newTopicName, setNewTopicName] = React.useState(topic?.name);
  const [user, loading] = useUserState();
  const [adminLoggedIn, setAdminLoggedIn] = React.useState(false);

  useEffect(() => {
    if (group && user) {
      setAdminLoggedIn(group.admin === user.uid);
    }
  }, [group, user]);

  useEffect(() => {
    if (groupId) {
      const documentReference = doc(getFirestore(), FirestoreConstants.GROUPS, groupId);
      getDoc(documentReference).then((documentSnapshot) => {
        if (documentSnapshot.exists()) {
          // console.log('Group data:', documentSnapshot.data());
          setGroup({ ...documentSnapshot.data(), id: documentSnapshot.id });
        } else {
          console.log('No such document!');
        }
      }).catch((error) => {
        console.log('Error getting document:', error);
      });
    }
  }, []);

  useEffect(() => {
    const topicReference = getTopicReference(topicId, groupId);
    getDoc(topicReference).then((documentSnapshot) => {
      if (documentSnapshot.exists()) {
        // console.log('Topic id: ', documentSnapshot.id, '; Topic data:', documentSnapshot.data());
        setTopic({ ...documentSnapshot.data(), id: documentSnapshot.id });
      } else {
        console.log('No such document!');
      }
    }).catch((error) => {
      console.log('Error getting document:', error);
    });
  }, []);

  // Statement/argument counts for the topic details subheader (see this project's Notion card).
  // Not supported for a personal topic yet (getTopicElementCounts mirrors getQuestionsQuery's
  // group/public split and throws for a group path on native -- see firebaseWrapper.native.ts),
  // so a failure here just leaves the subheader hidden instead of breaking the screen.
  useEffect(() => {
    getTopicElementCounts(topicId, groupId).then((counts) => {
      setTopicElementCounts(counts);
    }).catch((error) => {
      console.log('Error getting topic element counts:', error);
    });
  }, []);

  const handleTopicNameChange = (newName: string) => {
    const topicReference = doc(getFirestore(), FirestoreConstants.GROUPS, groupId, FirestoreConstants.TOPICS, topicId);
    if (newName && newName !== topic.name) {
      setDoc(topicReference, { name: newName }, { merge: true });
      setTopic({ ...topic, name: newName });
    }
    setTopicNameEditable(false);
  }

  const deleteTopic = () => {
    const performDelete = () => {
      const topicReference = doc(getFirestore(), FirestoreConstants.GROUPS, groupId, FirestoreConstants.TOPICS, topicId);
      const statementsWithTopic = query(collection(getFirestore(), FirestoreConstants.GROUPS, groupId, FirestoreConstants.STATEMENTS), where('topics', 'array-contains', topicId));
      getDocs(statementsWithTopic).then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          setDoc(doc.ref, { topics: arrayRemove(topicId) }, { merge: true });
        });
      }
      ).catch((error) => {
        console.log('Error getting documents: ', error);
      });
      deleteDoc(topicReference);
      navigation.goBack();
    };
    // The confirm/cancel actions of `alert()` fire asynchronously on native
    // (Alert.alert's onPress only runs once the user taps a button), so the
    // deletion must happen inside the confirm callback rather than behind a
    // synchronous flag checked right after calling alert().
    alert(strings.delete_topic, strings.delete_topic_alert, strings.cancel, () => { }, strings.delete_topic, performDelete);
  }

  return (
    <View
      flex
      style={{
        backgroundColor: Colors.primaryBG
      }}
    >
      <View flex
        style={{
          backgroundColor: isMobile ? 'transparent' : Colors.secondaryBG,
          borderRadius: isMobile ? 0 : 20,
          marginHorizontal: isMobile ? 0 : 5,
          marginVertical: 5,
          overflow: 'hidden',
        }}
      >
        {topic ?
          <View flex style={{ marginHorizontal: isMobile ? 0 : 10, marginVertical: 5, marginBottom: 0 }}>
            <View flex style={{ backgroundColor: 'transparent', borderRadius: 20, paddingVertical: isMobile ? 0 : 20 }}>
              <View paddingB-10 style={{ paddingHorizontal: isMobile ? 15 : 25 }}>
                {topicNameEditable ?
                  <View row centerV marginB-8>
                    <TextField
                      placeholder={topic.name}
                      value={newTopicName || topic.name}
                      onChangeText={currentName => setNewTopicName(currentName)}
                      style={{ color: Colors.textPrimary }}
                      h1
                    />
                    <IconButton
                      label={strings.cancel}
                      onPress={() => setTopicNameEditable(false)}
                      icon={<MaterialIcons name="close" size={25} color={Colors.iconPrimary} />}
                    />
                    <IconButton
                      label={strings.save}
                      onPress={() => handleTopicNameChange(newTopicName)}
                      icon={<MaterialIcons name="check" size={25} color={Colors.iconPrimary} />}
                    />
                  </View>
                  : <Text marginB-8 h1 style={{ color: Colors.textPrimary }}>
                    {topic.name}
                  </Text>
                }
                {topicElementCounts ?
                  <Text p marginB-8 style={{ color: Colors.textSecondary }}>
                    {strings.formatString(strings.topic_statement_count, topicElementCounts.statementCount)}
                    {'  ·  '}
                    {strings.formatString(strings.topic_argument_count, topicElementCounts.argumentCount)}
                  </Text>
                  : null
                }
                <View row>
                  {group ?
                    <Badge
                      customElement={<MaterialIcons name="people" size={16} color={Colors.iconSecondary} />}
                      label={group.name} size={20} backgroundColor={Colors.quaternaryBG} labelStyle={{ color: Colors.textSecondary }}
                      onPress={() => navigation.goBack()}
                    />
                    : null
                  }
                  {!topicNameEditable && adminLoggedIn ?
                    <Badge
                      customElement={<MaterialIcons name="mode-edit" size={16} color={Colors.iconSecondary} />}
                      label={strings.edit_topic_name} size={20} backgroundColor={Colors.quaternaryBG} labelStyle={{ color: Colors.textSecondary }}
                      onPress={() => setTopicNameEditable(true)}
                    />
                    : null
                  }
                  {topicNameEditable && adminLoggedIn ?
                    <Badge
                      customElement={<MaterialIcons name="delete" size={16} color={Colors.iconSecondary} />}
                      label={strings.delete_topic} size={20} backgroundColor={Colors.quaternaryBG} labelStyle={{ color: Colors.textSecondary }}
                      onPress={() => deleteTopic()}
                    />
                    : null
                  }
                </View>
              </View>
              {group || topic ?
                <QuestionViewList
                  navigation={navigation}
                  route={route}
                  location={location}
                  groupId={group?.id}
                  topicId={topic.id}
                  memberId={memberId}
                  groupAdminLoggedIn={adminLoggedIn}
                />
                : null}
            </View>
          </View>
          : null
        }
      </View >
      {location === PathConstants.GROUP || location === PathConstants.PERSONAL ?
        <FloatingActionButton
          navigation={navigation}
          parentInfo={parentInfo}
          possibleTypes={
            // Personal questions aren't implemented yet (see getQuestionsQuery), so this topic
            // screen still shows Statements there; Group questions are fully wired up.
            location === PathConstants.GROUP ? [ContainerType.QUESTION] : [ContainerType.STATEMENT]
          }
          groupId={groupId}
          topicId={topicId}
          memberId={memberId}
        />
        : null
      }
    </View >
  );
}